import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { getMembersBusySlots } from "../../../../context/stands/action";
import { useTranslation } from "react-i18next";
import { SelectOption } from "../../../../models/selectOptions";
import moment from "moment";
import { useFormik } from "formik";
import * as Yup from "yup";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import enGB from "date-fns/locale/en-GB";
import CustomSelect from "../../../../components/shared/forms/select/CustomSelect";
import Button from "../../../../components/shared/button/Button";
import CustomInputText from "../../../../components/shared/forms/input/CustomInputText";
import { useSelector } from "react-redux";
import { AuthState } from "../../../../context/auth/auth/reducer";
import { IToastContext, ToastContext } from "../../../../providers/Toast";
import {
  AppointmentStandMember,
  createNewAppointment,
} from "../../../../context/appointments/action";
import { Slot, Stand, UserSnippet } from "../../../../data/interfaces";
import useSlots from "../../../../hooks/useSlots";

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------
interface Props {
  stand: Stand;
  team: UserSnippet[];
}

registerLocale("en-GB", enGB);

const StandDetailGetAppointmentTabView = forwardRef(
  ({ stand, team, ...restProps }: Props, ref: any) => {
    // -----------------------------------------------------------------
    // L o c a l   v a r s
    // -----------------------------------------------------------------
    // Access to i18n instance that you may use to translate your content.
    const { t } = useTranslation();

    // Access to toast manager hook that you may use to show toasts.
    const { openToast } = useContext<IToastContext>(ToastContext);

    // Access to slots manager hook that you may use to handle available slots for team members.
    const { getFreeSlotsByDay, getSlotEndDateTime } = useSlots();

    // -----------------------------------------------------------------
    // f o r m
    // -----------------------------------------------------------------
    // Formik Initialisation data
    const form = useFormik({
      initialValues: {
        members: [],
        selectedSlot: undefined,
        notes: undefined,
        date: undefined,
      },
      validationSchema: Yup.object().shape({
        members: Yup.array().min(1, "Members are required").nullable(),
        selectedSlot: Yup.object().required("Slot is required!").nullable(),
        notes: Yup.string().required("Notes are required!").nullable(),
        date: Yup.string().required("Date is required!").nullable(),
      }),
      onSubmit: (values: any) => {
        // if (!!form.dirty) {
        handleGetAppointment(values);
        // }
      },
    });

    // -----------------------------------------------------------------
    // N a v i g a t i o n   v a r s
    // -----------------------------------------------------------------

    // -----------------------------------------------------------------
    // S t a t e
    // -----------------------------------------------------------------
    const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false);
    const [availableTimeSlots, setAvailableTimeSlots] = useState<Slot[] | null>(null);

    // -----------------------------------------------------------------
    // R e f s  (DOM)
    // -----------------------------------------------------------------
    useImperativeHandle(ref, () => ({
      selectMember(member: UserSnippet) {
        form.setFieldValue("members", mapTeamToOptions([member]), true);
      },
    }));

    // -----------------------------------------------------------------
    // u s e S e l e c t o r   m e t h o d s  (redux)
    // -----------------------------------------------------------------
    const { user } = useSelector(({ authReducer }: { authReducer: AuthState }) => {
      return authReducer;
    });

    // -----------------------------------------------------------------
    // W o r k i n g   m e t h o d s
    // -----------------------------------------------------------------

    const mapTeamToOptions = (team: UserSnippet[]): SelectOption[] => {
      if (team && team.length > 0) {
        const res: SelectOption[] = [];
        team.forEach((member, i) => {
          res.push({ value: member.uid, label: `${member.firstname} ${member.lastname}` });
        });
        return res;
      }
      return [];
    };

    const getMembersIds = (): string[] => {
      const res: string[] = [];

      form.values.members.forEach((member: SelectOption) => {
        res.push(member.value);
      });
      return res;
    };

    const canIProceed = (): boolean => {
      return !!form.values.date && form.values.members && form.values.members.length > 0;
    };

    const handleGetSlots = async () => {
      setIsLoadingSlots(true);

      try {
        form.setFieldValue("selectedSlot", []);
        const TIMING_SLOTS: string[] = await getMembersBusySlots(
          stand.id,
          getMembersIds(),
          moment(form.values.date).toISOString(true)
        );

        const AVAILABLE_SLOTS: Slot[] = getFreeSlotsByDay(moment(form.values.date), TIMING_SLOTS);

        setAvailableTimeSlots(AVAILABLE_SLOTS);
      } catch (err) {
        setAvailableTimeSlots([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };

    const handleGetAppointment = async (values: any) => {
      if (!user || !availableTimeSlots) {
        return;
      }
      const resMembers: AppointmentStandMember[] = [];

      values.members.forEach((member: SelectOption) => {
        const foundMember: UserSnippet | undefined = team.find((x) => x.uid === member.value);
        if (foundMember) {
          resMembers.push({
            uid: foundMember.uid,
            firstname: foundMember.firstname,
            lastname: foundMember.lastname,
            profile_image_url: foundMember.profile_image_url || "",
            email: foundMember.email,
          });
        }
      });

      const foundSlot: Slot | undefined = availableTimeSlots.find((x) =>
        moment(x.start_datetime).isSame(moment(values.selectedSlot.value))
      );

      if (!foundSlot) {
        return;
      }

      const appointmentStartDateTime: moment.Moment = moment(values.date)
        .clone()
        .set({
          hour: moment(foundSlot.start_datetime).get("hour"),
          minute: moment(foundSlot.start_datetime).get("minute"),
        });
      const appointmentEndDateTime: moment.Moment = moment(values.date)
        .clone()
        .set({
          hour: getSlotEndDateTime(foundSlot).get("hour"),
          minute: getSlotEndDateTime(foundSlot).get("minute"),
        });

      await createNewAppointment(
        user,
        resMembers,
        stand,
        appointmentStartDateTime.toISOString(true),
        appointmentEndDateTime.toISOString(true),
        values.notes
      );

      clearData();
      openToast({
        type: "success",
        title: t("pages.standDetail.tabs.appointments.toast.newAppointment.title"),
        message: t("pages.standDetail.tabs.appointments.toast.newAppointment.message"),
        callback: () => {},
      });
    };

    const clearData = () => {
      setAvailableTimeSlots(null);
      form.resetForm();
      form.setFieldValue("notes", null);
    };

    // -----------------------------------------------------------------
    // R e n d e r   m e t h o d s
    // -----------------------------------------------------------------

    // -----------------------------------------------------------------
    // L i f e c y c l e
    // -----------------------------------------------------------------
    /**
     * This method is called the first time the component is mounted
     * @function
     */
    const init = (): void => {
      // init component
      console.log("[StandDetailGetAppointmentTabView] init");
    };

    /**
     * This method is called when the component is unmounted
     * @function
     */
    const destroy = (): void => {
      // destroy component
      console.log("[StandDetailGetAppointmentTabView] destroy");
    };

    // -----------------------------------------------------------------
    // u s e E f f e c t   m e t h o d s
    // -----------------------------------------------------------------
    /**
     * This hook is called once when the component is mounted
     */
    useEffect(() => {
      init();
      return () => {
        destroy();
      };
    }, []);

    /**
     * Hook to form.values in order to reset form data
     */
    useEffect(() => {
      if (!canIProceed() && availableTimeSlots) {
        setAvailableTimeSlots(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.values]);

    // -----------------------------------------------------------------
    // T e m p l a t e
    // -----------------------------------------------------------------
    return (
      <div className={"panel-content get-appointment clearfix"}>
        <span>{t("pages.standDetail.tabs.appointments.form.info")}</span>
        <br />
        <br />
        <div className={"form-group"}>
          <div className={"row align-items-end"}>
            <div className="col-xs-12 col-sm-12 col-md-4 col-lg-4 col-xlg-5">
              <div className={`${form.touched.date && form.errors.date ? "is-invalid" : ""}`}>
                <label>{t("pages.standDetail.tabs.appointments.form.date.title")}</label>
                <DatePicker
                  selected={form.values.date}
                  minDate={moment().toDate()}
                  locale="en-GB"
                  isClearable
                  name="date"
                  placeholderText={t("pages.standDetail.tabs.appointments.form.date.placeholder")}
                  id="date"
                  className={"form-control"}
                  onChange={(event: any) => {
                    form.setFieldValue("date", event, true);
                    setAvailableTimeSlots(null);
                  }}
                  onBlur={(ev: any) => {
                    form.setFieldTouched("date", true, true);
                  }}
                />
                {form.touched.date && form.errors.date && (
                  <div className="invalid-feedback">{form.errors.date}</div>
                )}
              </div>
            </div>
            <div className="col-xs-12 col-sm-12 col-md-5 col-lg-5 col-xlg-5">
              <CustomSelect
                name="members"
                id="members"
                isMulti
                label={t("pages.standDetail.tabs.appointments.form.members")}
                options={mapTeamToOptions(team)}
                value={form.values.members}
                onChange={(event: any) => {
                  form.setFieldValue("members", event, true);
                  setAvailableTimeSlots(null);
                }}
                onBlur={(ev: any) => {
                  form.setFieldTouched("members", true, true);
                }}
                errors={form.errors.members}
                touched={form.touched.members}
              />
            </div>
            <div className="col-xs-12 col-sm-12 col-md-3 col-lg-3 col-xlg-2">
              <Button
                disabled={!canIProceed()}
                // loading={pending}
                onClick={handleGetSlots}
                submit={true}
                className="btn btn-form btn-primary full"
              >
                {t("pages.standDetail.tabs.appointments.form.checkSlots")}
              </Button>
            </div>
          </div>
        </div>

        {!!isLoadingSlots && (
          <div className="list-loading">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
        )}

        {!isLoadingSlots && availableTimeSlots && (
          <>
            <div className={"row"}>
              <div className="col-xs-12 col-sm-12">
                {availableTimeSlots.length > 0 ? (
                  <CustomSelect
                    name="selectedSlot"
                    id="selectedSlot"
                    label={t("pages.standDetail.tabs.appointments.form.time")}
                    options={availableTimeSlots.map((timeSlot: Slot) => ({
                      value: moment(timeSlot.start_datetime).toISOString(true),
                      label: `${moment(timeSlot.start_datetime).format(
                        "HH:mm"
                      )} - ${getSlotEndDateTime(timeSlot).format("HH:mm")}`,
                    }))}
                    value={form.values.selectedSlot}
                    onChange={(event: any) => {
                      form.setFieldValue("selectedSlot", event, true);
                    }}
                    onBlur={(ev: any) => {
                      form.setFieldTouched("selectedSlot", true, true);
                    }}
                    errors={form.errors.selectedSlot}
                    touched={form.touched.selectedSlot}
                  />
                ) : (
                  <span>{t("pages.standDetail.tabs.noData")}</span>
                )}
              </div>
            </div>
            <br />
          </>
        )}

        {!isLoadingSlots && availableTimeSlots && availableTimeSlots.length > 0 && (
          <div className={"row"}>
            <div className="col-xs-12 col-sm-12">
              <CustomInputText
                id="notes"
                name="notes"
                isTextarea
                label={t("pages.standDetail.tabs.appointments.form.notes")}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                defaultValue={form.values.notes}
                errors={form.errors.notes}
                touched={form.touched.notes}
              />
              <br />
              <Button
                disabled={!form.values.selectedSlot}
                // loading={pending}
                onClick={form.submitForm}
                submit={true}
                className="btn btn-form btn-primary pull-right"
              >
                {t("pages.standDetail.tabs.appointments.form.submit")}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default StandDetailGetAppointmentTabView;
